import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { AppTheme } from "~/contexts/AppTheme/appThemeConstants.ts";
import { APP_THEME_STORAGE_KEY, APP_THEMES, DEFAULT_APP_THEME } from "~/contexts/AppTheme/appThemeConstants.ts";

type State = {
    /** The theme that the user selects. */
    theme: AppTheme;
    /** A theme that a screen fixes while it shows, such as the presenter view. It is not stored. */
    forcedTheme: AppTheme | null;
    /** Shows the theme switcher at the right edge of each screen. */
    showEdgeSwitcher: boolean;
    setTheme: (theme: AppTheme) => void;
    setForcedTheme: (theme: AppTheme | null) => void;
    setShowEdgeSwitcher: (showEdgeSwitcher: boolean) => void;
};

export const useAppThemeStore = create<State>()(
    persist(
        (set) => ({
            theme: DEFAULT_APP_THEME,
            forcedTheme: null,
            showEdgeSwitcher: false,
            setTheme: (theme) => set({ theme }),
            setForcedTheme: (forcedTheme) => set({ forcedTheme }),
            setShowEdgeSwitcher: (showEdgeSwitcher) => set({ showEdgeSwitcher }),
        }),
        {
            name: APP_THEME_STORAGE_KEY,
            partialize: (state) => ({ theme: state.theme, showEdgeSwitcher: state.showEdgeSwitcher }),
            // A stored theme that no longer exists, such as "heute" or "klar", falls back to the default theme.
            merge: (persisted, current) => {
                const stored = (persisted ?? {}) as Partial<State>;
                const theme = APP_THEMES.find((item) => item === stored.theme) ?? DEFAULT_APP_THEME;
                return { ...current, ...stored, theme };
            },
        },
    ),
);

// The debug views show the screens in iframes. A switch in the top window reaches them through the storage event.
if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
        if (event.key === APP_THEME_STORAGE_KEY) void useAppThemeStore.persist.rehydrate();
    });
}

/** The theme that the app shows now. */
export const useActiveAppTheme = () => useAppThemeStore((state) => state.forcedTheme ?? state.theme);

/** Fixes the theme of the whole document while the calling component is mounted. */
export const useForcedAppTheme = (theme: AppTheme) => {
    const setForcedTheme = useAppThemeStore((state) => state.setForcedTheme);
    useEffect(() => {
        setForcedTheme(theme);
        return () => setForcedTheme(null);
    }, [theme, setForcedTheme]);
};
